import { useState } from 'react';
import {
  ShoppingBag,
  DollarSign,
  Clock,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Timer,
  ChefHat,
  Eye,
} from 'lucide-react';
import { StatCard, Card } from '@/components/ui';
import { useTranslation } from '@/hooks/useTranslation';
import { mockOrders } from '@/services/mockData';

// Cafe-specific mock data (for cafe_id = 1, "مقهى الورد")
const cafeStats = {
  todayOrders: 28,
  todayRevenue: 3450,
  avgPrepTime: 12,
  completionRate: 94,
  pendingOrders: 5,
  preparingOrders: 3,
  completedToday: 20,
  cancelledToday: 1,
  thisWeekRevenue: 18500,
  lastWeekRevenue: 16200,
  thisMonthOrders: 450,
  rating: 4.8,
  totalReviews: 234,
};

const hourlyOrders = [
  { hour: '07', orders: 2, revenue: 180 },
  { hour: '08', orders: 5, revenue: 420 },
  { hour: '09', orders: 8, revenue: 750 },
  { hour: '10', orders: 12, revenue: 1100 },
  { hour: '11', orders: 10, revenue: 920 },
  { hour: '12', orders: 15, revenue: 1450 },
  { hour: '13', orders: 11, revenue: 980 },
  { hour: '14', orders: 8, revenue: 680 },
  { hour: '15', orders: 6, revenue: 520 },
  { hour: '16', orders: 9, revenue: 830 },
  { hour: '17', orders: 7, revenue: 610 },
  { hour: '18', orders: 4, revenue: 340 },
];

const topProducts = [
  { name: { ar: 'كابتشينو', en: 'Cappuccino' }, orders: 45, revenue: 2025 },
  { name: { ar: 'لاتيه', en: 'Latte' }, orders: 38, revenue: 1900 },
  { name: { ar: 'كرواسون', en: 'Croissant' }, orders: 30, revenue: 1050 },
  { name: { ar: 'إسبريسو', en: 'Espresso' }, orders: 25, revenue: 750 },
  { name: { ar: 'تشيز كيك', en: 'Cheesecake' }, orders: 20, revenue: 700 },
];

export default function CafeDashboard() {
  const { t, formatCurrency, formatDateTime, language } = useTranslation();
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('today');

  // Filter orders for this cafe
  const cafeOrders = mockOrders.filter((o) => o.cafe_id === 1);
  const recentOrders = cafeOrders.slice(0, 5);

  const maxHourlyOrders = Math.max(...hourlyOrders.map((h) => h.orders));

  const statusColors: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    preparing: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
    on_the_way: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  const statusLabels: Record<string, Record<string, string>> = {
    pending: { ar: 'قيد الانتظار', en: 'Pending' },
    confirmed: { ar: 'مؤكد', en: 'Confirmed' },
    preparing: { ar: 'جاري التحضير', en: 'Preparing' },
    on_the_way: { ar: 'في الطريق', en: 'On the Way' },
    delivered: { ar: 'تم التوصيل', en: 'Delivered' },
    cancelled: { ar: 'ملغي', en: 'Cancelled' },
  };

  const weekChange = ((cafeStats.thisWeekRevenue - cafeStats.lastWeekRevenue) / cafeStats.lastWeekRevenue * 100).toFixed(1);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
            {language === 'ar' ? 'لوحة تحكم المقهى' : 'Cafe Dashboard'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar' ? 'مقهى الورد — نظرة عامة على الأداء' : 'Rose Cafe — Performance Overview'}
          </p>
        </div>
        <div className="flex gap-2">
          {(['today', 'week', 'month'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                timeRange === range
                  ? 'bg-amber-600 text-white shadow-lg shadow-amber-200 dark:shadow-amber-900/30'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-amber-300'
              }`}
            >
              {range === 'today' ? t('today') : range === 'week' ? t('thisWeek') : t('thisMonth')}
            </button>
          ))}
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={language === 'ar' ? 'طلبات اليوم' : "Today's Orders"}
          value={cafeStats.todayOrders}
          icon={<ShoppingBag size={24} />}
          color="primary"
          trend={{ value: 15, isUp: true }}
        />
        <StatCard
          title={language === 'ar' ? 'إيرادات اليوم' : "Today's Revenue"}
          value={formatCurrency(cafeStats.todayRevenue)}
          icon={<DollarSign size={24} />}
          color="success"
          trend={{ value: parseFloat(weekChange), isUp: true }}
        />
        <StatCard
          title={language === 'ar' ? 'متوسط التحضير' : 'Avg Prep Time'}
          value={`${cafeStats.avgPrepTime} ${language === 'ar' ? 'دقيقة' : 'min'}`}
          icon={<Timer size={24} />}
          color="warning"
        />
        <StatCard
          title={language === 'ar' ? 'نسبة الإكمال' : 'Completion Rate'}
          value={`${cafeStats.completionRate}%`}
          icon={<TrendingUp size={24} />}
          color="info"
        />
      </div>

      {/* Order Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-4 border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={18} className="text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
              {language === 'ar' ? 'معلق' : 'Pending'}
            </span>
          </div>
          <p className="text-2xl font-bold text-amber-800 dark:text-amber-200">{cafeStats.pendingOrders}</p>
        </div>

        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl p-4 border border-indigo-200 dark:border-indigo-800">
          <div className="flex items-center gap-2 mb-2">
            <ChefHat size={18} className="text-indigo-600 dark:text-indigo-400" />
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
              {language === 'ar' ? 'قيد التحضير' : 'Preparing'}
            </span>
          </div>
          <p className="text-2xl font-bold text-indigo-800 dark:text-indigo-200">{cafeStats.preparingOrders}</p>
        </div>

        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-4 border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400" />
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
              {language === 'ar' ? 'مكتمل' : 'Completed'}
            </span>
          </div>
          <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">{cafeStats.completedToday}</p>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-4 border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 mb-2">
            <XCircle size={18} className="text-red-600 dark:text-red-400" />
            <span className="text-sm font-medium text-red-700 dark:text-red-300">
              {language === 'ar' ? 'ملغي' : 'Cancelled'}
            </span>
          </div>
          <p className="text-2xl font-bold text-red-800 dark:text-red-200">{cafeStats.cancelledToday}</p>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <Card
          title={language === 'ar' ? 'آخر الطلبات' : 'Recent Orders'}
          className="lg:col-span-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700">
                  <th className="text-start p-4 text-sm font-semibold text-slate-500">#</th>
                  <th className="text-start p-4 text-sm font-semibold text-slate-500">{t('customer')}</th>
                  <th className="text-start p-4 text-sm font-semibold text-slate-500">{t('amount')}</th>
                  <th className="text-start p-4 text-sm font-semibold text-slate-500">{t('status')}</th>
                  <th className="text-start p-4 text-sm font-semibold text-slate-500">{t('date')}</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => (
                  <tr key={order.id} className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                    <td className="p-4 font-semibold text-slate-800 dark:text-slate-200">{order.order_number}</td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">{order.customer}</td>
                    <td className="p-4 font-semibold text-slate-800 dark:text-slate-200">{formatCurrency(order.total)}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${statusColors[order.status]}`}>
                        {statusLabels[order.status]?.[language] || order.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-500">{formatDateTime(order.created_at)}</td>
                    <td className="p-4">
                      <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Top Products */}
        <Card title={language === 'ar' ? 'الأكثر مبيعاً' : 'Top Sellers'}>
          <div className="p-4 space-y-4">
            {topProducts.map((product, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold
                  ${index === 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                    index === 1 ? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' :
                    index === 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                    'bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}
                >
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 dark:text-slate-200 truncate">
                    {product.name[language]}
                  </p>
                  <p className="text-xs text-slate-500">
                    {product.orders} {language === 'ar' ? 'طلب' : 'orders'}
                  </p>
                </div>
                <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
                  {formatCurrency(product.revenue)}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Hourly Orders Chart */}
      <Card title={language === 'ar' ? 'الطلبات حسب الساعة' : 'Orders by Hour'}>
        <div className="p-4">
          <div className="flex items-end gap-2 h-48">
            {hourlyOrders.map((h) => (
              <div key={h.hour} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                  {h.orders}
                </span>
                <div
                  className="w-full bg-gradient-to-t from-amber-500 to-amber-400 dark:from-amber-600 dark:to-amber-500 rounded-t-lg transition-all hover:from-amber-600 hover:to-amber-500"
                  style={{ height: `${(h.orders / maxHourlyOrders) * 100}%`, minHeight: '8px' }}
                />
                <span className="text-xs text-slate-400 dark:text-slate-500">{h.hour}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
